import { useCallback, useMemo, useState } from 'react'
import {
  useGetIndividualAssignments,
  createIndividualAssignment,
  deleteIndividualAssignment
} from '@/api/organizationIndividual'
import {
  createMemberAssignment,
  deleteAllMemberAssignments,
  deleteMemberAssignment,
  useGetMemberAssignments
} from '@/api/organizationMember'
import usePagination from '@/hooks/usePagination'
import toast from '@/utils/toast'
import type { IndividualAssignmentResponse } from '@/api/types/Organization'
import type { AssignedSubjectResponse } from '@/api/types/Subject'

import useFiltering from './useFiltering'

type IndividualAssignmentType = 'DELEGATE' | 'INNER_CIRCLE'
type AssignmentItem =
  | IndividualAssignmentResponse
  | AssignedSubjectResponse

type RemoveTarget =
  | { type: 'all' }
  | { type: 'single'; id: string }

interface UseAssignmentsProps {
  userId: string
  type: IndividualAssignmentType | 'INDIVIDUAL'
  paginationKey: string
  pageSize?: number
  successMessages?: {
    create: string
    delete: string
  }
  errorMessages?: {
    create: string
    delete: string
  }
}

export function useAssignments({
  userId,
  type,
  paginationKey,
  pageSize = 5,
  successMessages,
  errorMessages
}: UseAssignmentsProps) {
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [removeTarget, setRemoveTarget] = useState<RemoveTarget | null>(null)

  const isIndividualMode = type !== 'INDIVIDUAL'

  const { page, limit, setPage } = usePagination(pageSize, paginationKey)

  /* ---------- Assigned (existing) queries ---------- */

  const individualAssignedQuery = useGetIndividualAssignments({
    subjectId: userId,
    page,
    limit,
    status: 'assigned',
    type: type as IndividualAssignmentType
  }, isIndividualMode)

  const memberAssignedQuery = useGetMemberAssignments({
    memberId: userId,
    page,
    limit,
    status: 'assigned'
  }, !isIndividualMode)

  const assignedQuery = isIndividualMode
    ? individualAssignedQuery
    : memberAssignedQuery

  /* ---------- Available (unassigned) queries ---------- */

  const { updateFilterBy, allFilters } = useFiltering<['name']>()

  const individualAvailableQuery = useGetIndividualAssignments({
    subjectId: userId,
    page: 0,
    limit: 0,
    filterBy: allFilters.name,
    status: 'unassigned',
    type: type as IndividualAssignmentType
  }, isIndividualMode)

  const memberAvailableQuery = useGetMemberAssignments({
    memberId: userId,
    page: 0,
    limit: 0,
    filterBy: allFilters.name,
    status: 'unassigned'
  }, !isIndividualMode)

  const availableQuery = isIndividualMode
    ? individualAvailableQuery
    : memberAvailableQuery


  // Refetch all queries
  const refetchAll = useCallback(() => {
    return Promise.all([
      assignedQuery.refetch(),
      availableQuery.refetch()
    ])
  }, [assignedQuery, availableQuery])

  /* ---------- Create ---------- */

  const handleCreate = useCallback(
    async (body: Record<string, unknown> | FormData, isFormData = false) => {
      if (!userId) return false

      try {
        if (isIndividualMode) {
          await createIndividualAssignment(userId, body, isFormData)
        } else {
          await createMemberAssignment(userId, body as Record<string, unknown>)
        }

        toast.success(successMessages?.create)
        await refetchAll()
        return true
      } catch {
        toast.error(errorMessages?.create)
        return false
      }
    },
    [
      userId,
      isIndividualMode,
      refetchAll,
      successMessages?.create,
      errorMessages?.create
    ]
  )

  /* ---------- Delete ---------- */

  const handleDelete = useCallback(async () => {
    if (!userId || !removeTarget) return false

    try {
      if (isIndividualMode) {
        if (removeTarget.type !== 'single') return false

        await deleteIndividualAssignment(userId, {
          type,
          targetUserId: removeTarget.id
        })
      } else {
        if (removeTarget.type === 'all') {
          await deleteAllMemberAssignments(userId)
        } else {
          await deleteMemberAssignment(userId, {
            individualId: removeTarget.id
          })
        }
      }

      toast.success(successMessages?.delete)
      setShowDeleteModal(false)
      setRemoveTarget(null)

      await refetchAll()
      return true
    } catch {
      toast.error(errorMessages?.delete)
      return false
    }
  }, [
    userId,
    removeTarget,
    isIndividualMode,
    type,
    refetchAll,
    successMessages?.delete,
    errorMessages?.delete
  ])

  const openDeleteModal = useCallback((id?: string) => {
    setRemoveTarget(
      id ? { type: 'single', id } : { type: 'all' }
    )
    setShowDeleteModal(true)
  }, [])

  const closeDeleteModal = useCallback(() => {
    setShowDeleteModal(false)
    setRemoveTarget(null)
  }, [])

  /* ---------- Memoized output ---------- */

  const assignments = useMemo(
    () =>
      (assignedQuery.data?.data?.items as AssignmentItem[]) ?? [],
    [assignedQuery.data]
  )

  const availableAssignments = useMemo(
    () =>
      (availableQuery.data?.data?.items as AssignmentItem[]) ?? [],
    [availableQuery.data]
  )

  return {
    assignments,
    availableAssignments,
    updateAvailableAssignmentsSearch: updateFilterBy,
    pagination: assignedQuery.data?.data?.pagination,
    isLoadingAssignments: assignedQuery.isLoading,
    isLoadingAvailableAssignments: availableQuery.isLoading,
    page,
    setPage,
    handleCreate,
    handleDelete,
    openDeleteModal,
    closeDeleteModal,
    showDeleteModal,
    refetch: assignedQuery.refetch
  }
}
